import { t, type MessageKey } from '@ctcells/i18n'

import { cx } from '../atoms/cx.ts'
import { StageTrack } from '../atoms/StageTrack.tsx'
import { StatusBadge } from '../atoms/StatusBadge.tsx'
import { numberText } from '../screens/format.ts'

import type { BatchStripBatch, StripEstimate } from './batchStripTypes.ts'
import { StripClock } from './StripClock.tsx'
import {
  stripBriefIndex,
  stripCellIsNow,
  stripStageKnown,
  type StripPreflightPhase,
  type StripStageCell,
  type StripZoneCell,
} from './stripStages.ts'

/**
 * 작업 줄 **2행** — 디자인팀 시안 2026-09-30 §2 「공정 진행」(`docs/design/CTcells-console.html` `run()` · `brief()`).
 *
 * ```
 * full     ┌ ① 원심분리기 [완료] ┐┌ ② 어셈블러 [완료] ┐┌ ③ 염색기 [이동 중] ┐┌ 전체 1,127 / 4,730 스텝 ┐
 *          │ ████████████████  ││ ████████████████ ││ ███░░░░░░░░░░░░░  ││ 경과 00:23:18           │
 *          │ 176 / 176 스텝 100%││ 343 / 343 스텝 100%││ 608 / 4,211 스텝 14%││                         │
 * compact  [③ 염색기 [이동 중] 3 / 3 공정] ▇▇▇░░░░ 608 / 4,211 스텝 · 전체 1,127 / 4,730 · 경과 00:23:18   모니터링에서 보기
 * ```
 *
 * 🔴 **칸 셋은 템플릿 하나다** — 모니터링 단계 카드와 같은 뼈대(`atoms/stage.css` `.stage*` · `StageTrack`). 상태는
 *    `stage--*` 클래스 하나로만 갈리고 칸의 구성은 어느 상태에서도 같다(시안 「개발 규칙」). 완료 칸도 셈 · 100% 를 그대로 둔다.
 * 🔴 **색만으로 말하지 않는다** — 배지 글자(「완료」 · 「이동 중」 · 「대기」 · ▲「일시정지」) · 셈 · 막대 채움이 같은 사실을
 *    말한다. 흑백 앱에서는 면이 없고 막대 · 테 · 글자만 남는다.
 * 🔴 **높이는 CSS 가 정한다**(`batchStrip.css` `--strip-h-stages` · `--strip-h-brief`). 여기서 조건부로 줄을 늘리지 않는다 —
 *    값이 없으면 그 조각만 빈다.
 * 🔴 **재료는 모델이 준 것뿐이다**(`stripStages.ts`). 백분율은 모델이 낸 채움(`progress`)을 그대로 100 배 한 것이고
 *    (막대와 같은 수), 없는 수를 `0` 이나 `—` 로 채우지 않는다.
 */

/** 셈 — 「**176** / 176 스텝」 · 총수를 모르면 「**10** 스텝 · 전체 수량 집계 중」 · 아무 수도 모르면 「전체 수량 집계 중」 */
function StageCount({ cell, className }: { cell: StripStageCell; className: string }) {
  const unit = t('strip.stage.stepsUnit')
  if (stripStageKnown(cell)) {
    return (
      <span
        className={cx(className, 'num')}
        {...(cell.estimated === true ? { title: t('strip.moduleStepEstimated') } : {})}
      >
        <b>{numberText(cell.step ?? 0)}</b> / {numberText(cell.total)} {unit}
      </span>
    )
  }
  // 상태가 없으면(작업 없음 · 지금 자리 모름) 셈도 없다 — 빈 자리다
  if (cell.state === undefined) return <span className={className} />
  return (
    <span className={cx(className, 'num')}>
      {cell.step === undefined ? null : (
        <>
          <b>{numberText(cell.step)}</b> {unit} ·{' '}
        </>
      )}
      {t('monitoring.stage.totalPending')}
    </span>
  )
}

/** 막대의 접근 이름 — 「염색기 진행 608/4211」 · 「염색기 진행 전체 수량 집계 중」 */
function trackLabel(cell: StripStageCell): string {
  return t('strip.stage.progress', {
    module: cell.label,
    value: stripStageKnown(cell)
      ? `${String(cell.step ?? 0)}/${String(cell.total)}`
      : t('monitoring.stage.totalPending'),
  })
}

function StagePill({ cell }: { cell: Pick<StripStageCell, 'pill'> }) {
  if (cell.pill === undefined) return null
  return (
    <StatusBadge
      label={cell.pill.label}
      status={cell.pill.status}
      {...(cell.pill.title === undefined ? {} : { originalValue: cell.pill.title })}
    />
  )
}

/**
 * **멈춘 모듈 배지**(eric 2026-10-29 「중지 중이면 중지 중인 모듈에 중지 표시.. 일시정지는 주황색이지?」) — 그 모듈 묶음의 진행 칸 배지 자리에
 * 호박 알약 + ▲ 「일시정지」(작업 「중단」 · 시료 「폐기」와 같은 호박 계열 · 주의 톤). ▲ 는 배지 표지(`paused` 의 주의 심각도)가 그린다.
 * 🔴 전체 비상정지 · 안전정지는 여기 오지 않는다(`stripHaltAt` — 까닭 모듈이 없다 · 머리 줄 「안전정지」 칩이 말한다).
 */
function HaltPill() {
  return <StatusBadge label={t('strip.stage.pause')} status="paused" tone="warn" />
}

/**
 * 공정 칸 하나 — [번호 · 이름 · 배지] / 막대 / [셈 · %].
 * `inFlow`(통합 콘솔 · 준비 · 완료 영역과 한 줄) 이면 목록 항목(`li`)이고 **번호 표가 없다** — 차례는 목록 순서와 칸 사이 꺾쇠가
 * 말한다(eric 2026-10-06 「1,2,3 이게 맞나 싶기도 하고」 · 팀장 결정).
 */
function StripStageCell({
  cell,
  index,
  inFlow = false,
  halt,
}: {
  cell: StripStageCell
  index: number
  inFlow?: boolean
  /** 이 모듈이 멈춘 모듈이다(`BatchStripBatch.haltAt` · 서버 `halt_module_id`) — 진행 배지 자리에 멈춤 배지가 선다 */
  halt?: 'pause' | undefined
}) {
  const known = stripStageKnown(cell)
  // 작업은 있는데 총수를 모른다 — 빗금 막대(「집계 중」을 모양으로도). 돌고 있는데 자리를 모르는 칸(`active`)도 같은 모양이다
  const pending = (cell.state !== undefined || cell.active === true) && !known
  return (
    <div className={cx('strip__stage', 'stage', `stage--${cell.state ?? 'wait'}`)}>
      <span className="stage__head">
        {/* 번호 표 — 차례는 공정 순서다. 이름이 곧 뜻이라 보조기술에는 숨긴다 */}
        {inFlow ? null : (
          <span aria-hidden="true" className="stage__mark">
            {String(index + 1)}
          </span>
        )}
        {/* 묶음 안(`inFlow`)에서는 막대 위 「진행」 — 장비 이름은 묶음 머리(「01 원심분리기」)가 말한다. 접근 이름은 「원심분리기 진행」 */}
        <span className="stage__name" {...(cell.title === undefined ? {} : { title: cell.title })}>
          {inFlow ? (
            <>
              <span aria-hidden="true">{t('strip.zone.run')}</span>
              <span className="a-sr">{t('strip.zone.runName', { module: cell.label })}</span>
            </>
          ) : (
            cell.label
          )}
        </span>
        {halt === undefined ? <StagePill cell={cell} /> : <HaltPill />}
        {/* 트레이 격리 · 낙하 — 서버가 «떠나기 전 모듈»로 말한 이 칸에만(`holdPill`) */}
        {cell.hold === undefined ? null : <StagePill cell={{ pill: cell.hold }} />}
      </span>
      <StageTrack
        label={trackLabel(cell)}
        pending={pending}
        {...(known ? { ratio: cell.progress } : {})}
      />
      <span className="stage__meta">
        <StageCount cell={cell} className="stage__count" />
        {known ? (
          <span className="stage__pct num">{`${String(Math.round(cell.progress * 100))}%`}</span>
        ) : null}
      </span>
    </div>
  )
}

/**
 * 영역 칸 하나(준비 · 완료) — 장비 칸과 같은 뼈대: [이름 · 배지] / 막대 / [빈 셈 줄](eric 2026-10-06 「대기도.. 원심분리기랑..
 * 어셈블러맞게.. 프로그레스바」 → 「완료가.. 프로그레스바 오른쪽 위에」). 장비 칸보다 좁다. 목록 항목(`li`)이다. 눈에는 「준비」 한 마디
 * (다음 칸이 장비 이름을 말한다), 접근 이름 · 툴팁은 「원심분리기 준비」(`name`).
 * 🔴 **막대에 값을 지어내지 않는다** — 준비 칸에는 셈도 백분율도 없다(서버가 안 준다). 모양 셋만(`zoneTrack`):
 *    아직 = 빈 막대 · 지금(준비 중 · 적재 대기 · 멈춤) = 빗금(장비 칸의 «돌지만 자리를 모를 때»와 같은 모양) · 지남/완료 = 채운 막대.
 * 🔴 상태는 장비 칸과 같은 `stage--*` 클래스 하나로 갈린다 — 지금인 영역은 장비 칸과 같은 강조 면이다(«지금»은 하나다).
 * 🔴 색만으로 말하지 않는다 — 배지 글자(「준비 중」 · 「적재 대기」 · 「완료」)와 막대 모양이 같이 말한다. 모르면 배지가 없다.
 */
/**
 * 묶음 머리 — 「01 원심분리기」 · 「04 완료」(eric 2026-10-12 「01 원심분리기고 하고.. 각각의 프로그레스바에 준비와 진행」). 번호는 장비 단위
 * (eric 「1.0 준비 1.1 원심분리기..」 → 장비 단위 번호를 골랐다) — 장비 판 단계 띠(「01 작업 전」)와 같은 꼴.
 * 장식이다 — 칸마다 접근 이름(「원심분리기 준비」 · 「원심분리기 진행」)이 있고 차례는 목록 순서가 말해 보조기술에는 숨긴다
 */
function GroupHead({ no, title }: { no: string; title: string }) {
  return (
    <span aria-hidden="true" className="strip__grouphead">
      <span className="stage__no num">{no}</span>
      <span className="strip__grouptitle" title={title}>
        {title}
      </span>
    </span>
  )
}

const groupNoText = (n: number) => String(n).padStart(2, '0')

/**
 * 「00 사전 점검」 칸의 글자 · 기호 · 칸 상태(`stage--*`) · 막대. 강조는 점검이 «지금»인 동안(창이 열린 동안)만.
 * 🔴 막대는 진행률을 짓지 않는다 — 사전 점검 응답(`PreflightResult`)은 `passed` · `blockers` · `warnings` · `passed_by_setting` ·
 *    `full_counts` 뿐이고 «전체 점검 항목 수»가 없다(걸린 것만 온다 · openapi 실측 2026-10-29). 그래서 «통과 n / 전체 m» 을 못 센다 —
 *    모양 넷만: 평소 = 빈 막대 · 점검 중 = 움직이는 빗금(«미정 진행» · 준비 칸 「지금」과 같은 빗금) · 결과 = 가득(막힘은 회색 채움 + ▲) ·
 *    시작 뒤 = 가득(완료 칸과 같은 채움).
 */
const PREFLIGHT_LOOK: Record<
  StripPreflightPhase,
  {
    text: MessageKey | null
    glyph: string | null
    /** 배지 모양을 빌릴 원값(`StatusBadge` 표기 종류) — 글자는 `text` 다 */
    pill: string | null
    state: 'run' | 'wait' | 'done'
    track: 'empty' | 'moving' | 'full'
  }
> = {
  idle: { text: null, glyph: null, pill: null, state: 'wait', track: 'empty' },
  checking: {
    text: 'strip.preflight.checking',
    glyph: null,
    pill: 'running',
    state: 'run',
    track: 'moving',
  },
  passed: {
    text: 'strip.preflight.passed',
    glyph: '✓',
    pill: 'running',
    state: 'run',
    track: 'full',
  },
  /* 막힘 — 위험 · 주의 배지(빨강 · 호박 ▲ 표지)를 빌리지 않는다. 글자 「막힘 ▲」가 말하고 모양은 중성(대기 배지) */
  blocked: {
    text: 'strip.preflight.blocked',
    glyph: '▲',
    pill: 'IDLE',
    state: 'run',
    track: 'full',
  },
  /* 완료 — 다른 칸의 「완료」 배지와 같은 모양 · 같은 글자(eric 2026-10-29 「다른 원심분리기나 그거처럼 오른쪽에 완료」) */
  done: {
    text: 'strip.stage.done',
    glyph: null,
    pill: 'completed',
    state: 'done',
    track: 'full',
  },
}

/**
 * 「00 사전 점검」 묶음 — 01 왼쪽 · 다른 묶음과 같은 머리(번호 + 이름 굵게) · 같은 높이(eric 2026-10-29). 칸은 준비 · 완료 칸과 같은
 * 뼈대다: [상태 글자] / 막대 / [빈 셈 줄]. 셈 · 백분율은 없다(위 `PREFLIGHT_LOOK` — 응답에 전체 항목 수가 없다).
 * 🔴 강조는 장비 칸 «진행 중»과 같은 `stage--run` 클래스(같은 토큰)다. 색만으로 말하지 않는다 — 글자(「점검 중」 · 「통과」 · 「막힘」 ·
 *    「완료」)와 기호(✓ · ▲) · 막대 모양이 같이 말한다. 평소(점검 전)는 상태 글자를 비운다(eric 2026-10-29).
 */
function StripPreflight({ phase }: { phase: StripPreflightPhase }) {
  const look = PREFLIGHT_LOOK[phase]
  const title = t('strip.preflight.title')
  const word = look.text === null ? null : t(look.text)
  return (
    <li className="strip__group strip__group--preflight">
      <GroupHead no={groupNoText(0)} title={title} />
      <div
        className={cx(
          'strip__stage',
          'strip__pf',
          `strip__pf--${phase}`,
          'stage',
          `stage--${look.state}`,
        )}
        data-preflight={phase}
      >
        {/*
          🔴 상태는 머리 줄 오른쪽 배지 — 준비 · 장비 칸의 「대기」 · 「완료」 배지와 같은 자리 · 같은 컴포넌트(eric 2026-10-29 「완료면.. 다른
             원심분리기나 그거처럼 오른쪽에 완료라고 해야지」). 점검 중 · 통과 · 막힘도 같은 자리로 옮겼다(글자 · 기호는 그대로). 아래 따로 서던
             「완료 ✓」 글자 줄은 걷었다. 평소(점검 전)는 배지 없이 줄만 남는다 — 줄이 접히면 막대가 01~04 칸보다 위로 올라간다
        */}
        <span className={cx('stage__head', 'strip__pfstate', `strip__pfstate--${phase}`)}>
          <span className="a-sr">{title} </span>
          {word === null || look.pill === null ? (
            <span aria-hidden="true">{'\u00a0'}</span>
          ) : (
            <StatusBadge
              label={look.glyph === null ? word : `${word} ${look.glyph}`}
              status={look.pill}
            />
          )}
        </span>
        <StageTrack
          className={cx('strip__pftrack', look.track === 'moving' && 'strip__pftrack--moving')}
          label={word === null ? title : `${title} · ${word}`}
          pending={look.track === 'moving'}
          {...(look.track === 'full' ? { ratio: 1 } : {})}
        />
        <span className="strip__zonemeta stage__meta" />
      </div>
    </li>
  )
}

function StripZone({ zone }: { zone: StripZoneCell }) {
  const track = zoneTrack(zone)
  return (
    <div
      className={cx(
        'strip__stage',
        'strip__zone',
        `strip__zone--${zone.kind}`,
        'stage',
        `stage--${zone.state ?? 'wait'}`,
      )}
      data-zone={zone.key}
    >
      <span className="stage__head">
        <span className="stage__name strip__zonename" title={zone.name}>
          {/* 완료 칸은 묶음 머리(「04 완료」)가 이미 말해 눈 글자를 안 겹친다 — 접근 이름은 그대로 */}
          {zone.kind === 'done' ? null : <span aria-hidden="true">{zone.label}</span>}
          <span className="a-sr">{zone.name}</span>
        </span>
        <StagePill cell={zone} />
      </span>
      <StageTrack
        label={track.label}
        pending={track.pending}
        {...(track.ratio === undefined ? {} : { ratio: track.ratio })}
      />
      {/*
        셈 줄 자리 — 준비 · 완료에는 이 배치의 스텝 수가 없다(6축 진행 프레임에 `batch_id` 가 없고 적재 · 발송은 스텝이 아니다).
        🔴 끝난 칸만 「100%」(eric 2026-10-12 「준비가 완료되면.. 100% 라는 것을 표시해줘」) — 서버가 그 영역을 «지남 · 완료»라 말한 사실이다.
           진행 중 · 아직은 숫자를 짓지 않는다(빗금 · 빈 막대)
      */}
      <span className="strip__zonemeta stage__meta">
        {zone.state === 'done' ? <span className="stage__pct num">100%</span> : null}
      </span>
    </div>
  )
}

/**
 * 준비 · 완료 칸 막대의 모양 — 상태에서만 나온다(값 없음). 지남 · 완료(`done`)면 채움, 지금(`run` · `pause` · `stop`)이면 빗금,
 * 아직(`wait`) · 모름(상태 없음)이면 빈 막대. 접근 이름은 「원심분리기 준비 · 준비 중」처럼 이름과 배지 글자(없으면 상태 이름)다.
 */
function zoneTrack(zone: StripZoneCell): { pending: boolean; ratio?: number; label: string } {
  const word =
    zone.pill?.label ?? (zone.state === undefined ? undefined : t(`strip.stage.${zone.state}`))
  const label = word === undefined ? zone.name : `${zone.name} · ${word}`
  if (zone.state === 'done') return { pending: false, ratio: 1, label }
  if (stripCellIsNow(zone)) return { pending: true, label }
  return { pending: false, label }
}

/**
 * 오른쪽 요약 카드 — 「스텝 **1,127** / 4,730」 · 「시간 00:23:18 / 예상」.
 * 🔴 값이 없는 줄은 그 줄만 빈다(`—` 로 채우지 않는다).
 * 🔴 **작업이 없으면 같은 자리 · 같은 면 · 같은 크기의 빈 카드다**(eric 2026-10-29 「04 완료 오른쪽에.. 여기에도 사각형을 넣어줘」 →
 *    같은 날 「진행 중이지 않을 때.. 이거 스텝.. 시간 없애줘」) — 스텝 · 시간 줄 없이 속을 비운다. 0 · 00:00:00 · 「–」 를 채우지 않는다
 *    (`IdleTally`). 작업이 있을 때의 모양은 그대로다.
 * 🔴 돌고 있지 않으면(일시정지 · 중단 · 끝남 · 시작 전) 경과가 흐린 글자다 — 「멈춘 시계」로 읽히게(시안 `.frozen`).
 *    초를 세는 일은 그대로 `StripClock` 이 한다(멈춤에도 경과가 흐르는 모델 규칙은 안 바꿨다).
 */
export function StripTally({
  batch,
  action = null,
  estimate,
}: {
  batch: BatchStripBatch | null | undefined
  /** 「예상」 줄(통합 콘솔) — 경과와 같은 줄 · 단추 왼쪽. 작업이 없으면 그리지 않는다(`IdleTally`) */
  estimate?: StripEstimate | undefined
  /**
   * 카드 **오른쪽 아래** 구석의 단추 하나(통합 콘솔의 이벤트 로그 · `BatchStripProps.eventsInTally`). 경과 줄과 같은 줄,
   * 오른끝이다. 🔴 카드 높이를 늘리지 않는다 — 두 줄짜리 카드의 둘째 줄 높이 안에 선다(`batchStrip.css` `.strip__evtog--tally`).
   */
  action?: React.ReactNode
}) {
  if (batch == null) return <IdleTally action={action} />
  const totals = batch.totalSteps
  const empty = totals === undefined && batch.elapsedText === undefined
  const frozen = batch.status !== 'running'
  const showEstimate = estimate !== undefined
  // 🔴 값이 하나도 없는 줄은 그리지 않는다(eric 2026-10-29 「진행 중이지 않을 때.. 스텝.. 시간 없애줘」) — 경과도 서버 예상도 없으면 시간 줄이 없다.
  //    줄이 다 빠지면 작업 없을 때와 같은 빈 카드다(대기 작업 등)
  const showTime = batch.elapsedText !== undefined || estimate?.text !== undefined
  if (totals === undefined && !showTime) return <IdleTally action={action} />
  return (
    <div
      className={cx(
        'strip__tally',
        frozen && 'strip__tally--frozen',
        empty && 'strip__tally--empty',
        action != null && action !== false && 'strip__tally--action',
      )}
    >
      {totals === undefined ? null : (
        <span className="strip__tallyrow strip__tallyrow--main">
          <span className="strip__tallylab">{t('strip.tallySteps')}</span>
          <span className="strip__count num" title={totalStepTitle(batch)}>
            <span className="a-sr">{t('strip.totalStep', { value: '' })}</span>
            <b className="strip__tallybig">{numberText(totals.step)}</b>
            <span className="strip__tallyv">
              {totals.total === undefined
                ? t('monitoring.stage.totalPending')
                : `/ ${numberText(totals.total)}${batch.stepCountPartial === true ? '+?' : ''}`}
            </span>
          </span>
        </span>
      )}
      {!showTime ? null : (
        <span className="strip__tallyrow strip__tallyrow--time">
          <span className="strip__tallylab">{t('strip.tallyTime')}</span>
          <span className="strip__timevalues">
            {batch.elapsedText === undefined ? (
              '–'
            ) : (
              <StripClock
                kind={batch.elapsedKind}
                sinceMs={batch.elapsedSinceMs}
                text={batch.elapsedText}
                totalText={showEstimate ? undefined : batch.totalText}
              />
            )}
            {/* 서버 예상값만 쓴다. 없으면 짧은 대시(eric 2026-10-27), 비례 계산은 하지 않는다. */}
            {!showEstimate ? null : (
              <>
                {' / '}
                <span
                  className={cx(
                    'strip__estimate',
                    'num',
                    estimate.text === undefined && 'strip__estimate--none',
                  )}
                  title={estimate.reason}
                >
                  {estimate.text ?? '–'}
                  <span className="a-sr">{estimate.reason}</span>
                </span>
              </>
            )}
          </span>
        </span>
      )}
      {action}
    </div>
  )
}

/**
 * 작업이 없을 때의 요약 카드 — 작업 있을 때와 같은 자리 · 같은 면 · 같은 크기의 **빈 카드**다(eric 2026-10-29 「진행 중이지 않을 때..
 * 이거 스텝.. 시간 없애줘」). 스텝 · 시간 줄을 그리지 않는다. 높이는 줄 수가 아니라 작업 줄 격자가 쥔다(01~04 칸과 같은 높이).
 * `action`(이벤트 로그 단추)이 있으면 그대로 오른쪽 아래다(`.strip__tally--idle`).
 */
function IdleTally({ action }: { action: React.ReactNode }) {
  return (
    <div
      className={cx(
        'strip__tally',
        'strip__tally--frozen',
        'strip__tally--idle',
        action != null && action !== false && 'strip__tally--action',
      )}
    >
      {action}
    </div>
  )
}

/**
 * 전체 스텝 수의 툴팁 — 「전체 스텝 1127/4730」 + 분모가 늘 수 있다는 사실(`+?`) + 예상이 섞였다는 사실.
 * 🔴 예상이 섞였으면 숫자는 깔끔하게 두고 툴팁만 말한다(eric 2026-09-28).
 */
function totalStepTitle(batch: BatchStripBatch): string {
  return [
    t('strip.totalStep', { value: batch.stepCountText ?? '' }),
    batch.stepCountPartial === true ? t('strip.totalStepPartial') : undefined,
    batch.stepCountEstimated === true ? t('strip.totalStepEstimated') : undefined,
  ]
    .filter((part) => part !== undefined)
    .join(' · ')
}

/** `full` — 공정 칸 셋 + 요약 카드. `cells` 는 `stripStageCells(batch)` — 1행과 같은 재료라 작업 줄이 한 번 만들어 넘긴다 */
export function StripStages({
  batch,
  cells,
  zones,
  tallyAction = null,
  estimate,
  preflight = 'idle',
}: {
  batch: BatchStripBatch | null | undefined
  cells: readonly StripStageCell[]
  /**
   * 준비 · 완료 영역(`stripFlowZones`) — 있으면 `[대기] 원심 [대기] 어셈 [대기] 염색 [완료]` 로 끼운다(통합 콘솔 `flowZones`).
   * 없으면 종전 칸 셋이다.
   */
  zones?: readonly StripZoneCell[] | undefined
  /** 요약 카드 오른쪽 아래의 단추(`StripTally` `action`) — 통합 콘솔의 이벤트 로그 */
  tallyAction?: React.ReactNode
  /** 요약 카드 「예상」 줄(`StripTally` `estimate`) — 통합 콘솔 */
  estimate?: StripEstimate | undefined
  /** 「00 사전 점검」 칸(영역을 켠 셸만 · `BatchStripProps.preflight`) */
  preflight?: StripPreflightPhase | undefined
}) {
  if (zones === undefined) {
    return (
      <div className="strip__stages">
        {cells.map((cell, index) => (
          <StripStageCell cell={cell} index={index} key={cell.key} />
        ))}
        <StripTally action={tallyAction} batch={batch} estimate={estimate} />
      </div>
    )
  }
  /*
   * 🔴 **대기 · 장비 · 완료는 한 목록(`ol`)이다** — 차례는 목록 순서가 말하고, 칸 사이의 꺾쇠(›)는 CSS 가 그리는 장식이라 읽히지
   *    않는다(`batchStrip.css` `.strip__flow`). 목록 상자는 격자에서 빠져(`display: contents`) 칸들이 2행 격자의 칸으로 바로 선다.
   */
  const doneZone = zones.find((zone) => zone.kind === 'done')
  return (
    <div className="strip__stages strip__stages--zones">
      <ol className="strip__flow" aria-label={t('strip.flowLabel')}>
        <StripPreflight phase={preflight} />
        {cells.map((cell, index) => {
          const prep = zones.find((zone) => zone.kind === 'prep' && zone.before === index)
          /* 묶음 하나 = «준비 + 장비» — 머리 「01 원심분리기」 아래 막대 둘(준비 · 진행)이 얇은 선 상자 하나에 선다 */
          const no = groupNoText(index + 1)
          /*
           * 그 모듈이 지금 돈다 — 묶음 안 준비 또는 진행 칸이 «진행»(`stage--run`)이다(eric 2026-10-29 「해당 모듈 실행 중일 때.. 숫자에 강조색 주고..
           * 모듈 전체 섹션에 바탕색」). 상태는 칸이 이미 받은 값 그대로 — 새로 짐작하지 않는다
           */
          /* 멈춘 모듈(서버 `halt_module_id` · `stripHaltAt`) — 그 묶음은 «도는» 면을 걷고 진행 배지 자리에 멈춤 배지 */
          const halt = batch?.haltAt?.key === cell.key ? batch.haltAt.kind : undefined
          const running = halt === undefined && (cell.state === 'run' || prep?.state === 'run')
          return (
            <li
              className={cx(
                'strip__group',
                prep !== undefined && 'strip__group--pair',
                running && 'strip__group--run',
                halt !== undefined && 'strip__group--halt',
              )}
              data-halt={halt}
              key={cell.key}
            >
              <GroupHead no={no} title={cell.label} />
              {prep === undefined ? null : <StripZone zone={prep} />}
              <StripStageCell cell={cell} halt={halt} index={index} inFlow />
            </li>
          )
        })}
        {doneZone === undefined ? null : (
          <li className="strip__group">
            <GroupHead no={groupNoText(cells.length + 1)} title={doneZone.label} />
            <StripZone zone={doneZone} />
          </li>
        )}
      </ol>
      <StripTally action={tallyAction} batch={batch} estimate={estimate} />
    </div>
  )
}

/**
 * `compact` — 목록 화면(작업 · 이력 · 알람 · 준비 · 관리)의 축약형(시안 `brief()`). 현재 공정 한 줄:
 * `[번호][이름][배지] 2 / 3 공정 · 막대 · 608 / 4,211 스텝 · 전체 1,127 / 4,730 · 경과 00:23:18 · 모니터링에서 보기`.
 * 데이터와 상태 규칙은 위 공정 칸과 같다(`stripStageCells`) — 보이는 칸이 하나일 뿐이다.
 * 🔴 「모니터링에서 보기」는 셸이 이동을 넘겼을 때만 선다(죽은 버튼 금지 · `docs/shell.md` §3-0).
 * 🔴 **폭 불변** — 한 줄은 CSS 격자(`batchStrip.css` `.strip__brief` `grid-template-areas`)이고 조각마다 제 자리
 *    (`grid-area`)가 있다. 배지 문구가 스텝마다 길어지고 · 셈이 자릿수를 넘고 · 시계가 초를 세도 오른쪽이 안 밀린다.
 *    그래서 머리 조각(번호 · 이름 · 배지)을 한 상자로 묶지 않는다 — 묶으면 배지가 그 상자를 늘려 오른쪽 전부를 민다.
 *    조각이 빠져도(전체 · 경과 · 링크가 없을 때) 자리 이름이 있어 나머지가 앞으로 당겨지지 않는다.
 */
export function StripBrief({
  batch,
  cells,
  zones,
  onOpenMonitoring,
  slim = false,
}: {
  batch: BatchStripBatch | null | undefined
  cells: readonly StripStageCell[]
  /**
   * 준비 · 완료 영역(통합 콘솔 `flowZones`). **준비 영역이 지금이면** 한 줄의 이름 · 배지가 그 영역을 말한다
   * (「어셈블러 준비 [준비 중]」) — 차례 · 막대는 그 뒤 장비 칸 것이다. 지금인 영역이 없으면 종전 그대로다.
   */
  zones?: readonly StripZoneCell[] | undefined
  onOpenMonitoring: (() => void) | undefined
  /**
   * **접힌 작업 줄 안**(2026-10-01 회의) — 1행 안에 끼는 한 단 얇은 꼴. 같은 조각(번호 · 이름 · 배지 · n / 3 공정 · 막대 ·
   * 전체)이고 공정 셈 · 경과 · 「모니터링에서 보기」만 빠진다. 2행 상자(`.strip__stages`)로 감싸지 않는다 — 1행의 조각이다.
   * 작업이 없으면 아무것도 안 그린다(1행 높이가 줄 높이라 빈 자리가 필요 없다).
   * 🔴 폭이 모자라면 **뒤 조각부터 통째로 빠진다**(막대가 최소 폭까지 준 뒤 전체 → 막대 순 · `batchStrip.css` 접힘 절) —
   *    조각이 반쯤 잘려 서지 않는다. 번호 · 이름 · 배지 · 「n / 3 공정」이 끝까지 남는다.
   */
  slim?: boolean
}) {
  const nowZone = zones?.find((zone) => zone.kind === 'prep' && stripCellIsNow(zone))
  const index = nowZone?.before ?? stripBriefIndex(cells)
  const moduleCell = cells[index]
  /* 지금인 준비 영역이 있으면 이름 · 상태 · 배지만 그 영역 것으로 — 막대 · 셈 · 툴팁은 그 뒤 장비 칸 것을 그대로 둔다 */
  const cell: StripStageCell | undefined =
    moduleCell === undefined || nowZone === undefined
      ? moduleCell
      : {
          ...moduleCell,
          label: nowZone.name,
          ...(nowZone.state === undefined ? {} : { state: nowZone.state }),
          ...(nowZone.pill === undefined ? {} : { pill: nowZone.pill }),
        }
  if (slim && (batch == null || cell === undefined)) return null
  if (batch == null || cell === undefined) {
    // 작업이 없으면 빈 줄이다 — 행 높이는 CSS 가 쥐고 있어 아래가 안 밀린다
    return (
      <div className="strip__stages strip__stages--brief">
        <div className="strip__brief strip__brief--empty" />
      </div>
    )
  }
  const known = stripStageKnown(cell)
  const totals = batch.totalSteps
  const frozen = batch.status !== 'running'
  /* 경과 — 펼친 축약형은 격자 자리(`elapsed`)로 전체 뒤, 접힌 줄은 전체 앞 */
  const elapsed =
    batch.elapsedText === undefined ? null : (
      <span className="strip__briefkv strip__briefkv--elapsed">
        <span>{t('strip.tallyElapsed')}</span>
        <StripClock
          kind={batch.elapsedKind}
          sinceMs={batch.elapsedSinceMs}
          text={batch.elapsedText}
          totalText={batch.totalText}
        />
      </span>
    )
  const line = (
    <div
      className={cx(
        'strip__brief',
        slim && 'strip__brief--slim',
        'stage',
        `stage--${cell.state ?? 'wait'}`,
        frozen && 'strip__tally--frozen',
      )}
    >
      {/* 번호 표 — 준비 · 완료 영역을 켠 셸(통합 콘솔)은 번호를 안 쓴다(2행과 같은 규칙 · 「n / 3 공정」이 차례를 말한다) */}
      {zones === undefined ? (
        <span aria-hidden="true" className="stage__mark">
          {String(index + 1)}
        </span>
      ) : null}
      <span className="stage__name" {...(cell.title === undefined ? {} : { title: cell.title })}>
        {cell.label}
      </span>
      {/* 접힌 줄은 격자가 아니라 접어 넘기는 줄이라 배지 칸 고정 폭을 상자 하나가 쥔다(`.strip__briefpill`) */}
      {slim ? (
        <span className="strip__briefpill">
          <StagePill cell={cell} />
        </span>
      ) : (
        <StagePill cell={cell} />
      )}
      <span className="strip__briefof num">
        {t('strip.briefOf', { index: String(index + 1), count: String(cells.length) })}
      </span>
      <StageTrack
        className="strip__briefline"
        label={trackLabel(cell)}
        pending={cell.state !== undefined && !known}
        {...(known ? { ratio: cell.progress } : {})}
      />
      {slim ? null : <StageCount cell={cell} className="strip__briefcount" />}
      {/* 접힌 줄은 「경과」가 「전체」 앞이다(eric 2026-10-12 「상태가 줄인 화면인데.. 전체 스텝 앞에. 경과 시간도 넣어줘」) — 넘치면 뒤(전체)부터 빠진다 */}
      {slim ? elapsed : null}
      {totals === undefined ? null : (
        <span className="strip__briefkv strip__briefkv--total">
          <span>{t('strip.tallyTotal')}</span>
          <span className="strip__count num" title={totalStepTitle(batch)}>
            <span className="a-sr">{t('strip.totalStep', { value: '' })}</span>
            <b>{numberText(totals.step)}</b>
            {totals.total === undefined
              ? ` ${t('strip.stage.stepsUnit')} · ${t('monitoring.stage.totalPending')}`
              : ` / ${numberText(totals.total)}${batch.stepCountPartial === true ? '+?' : ''}`}
          </span>
        </span>
      )}
      {slim ? null : elapsed}
      {slim || onOpenMonitoring === undefined ? null : (
        <button className="lnk strip__briefgo" onClick={onOpenMonitoring} type="button">
          {t('action.openMonitoring')}
        </button>
      )}
    </div>
  )
  return slim ? line : <div className="strip__stages strip__stages--brief">{line}</div>
}
